import type { ConnectedPosition, OverlayRef } from '@angular/cdk/overlay';
import { Overlay } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import type { AfterViewInit, OnDestroy, OnInit } from '@angular/core';
import { Directive, ElementRef, Input, NgZone, Renderer2, TemplateRef, ViewContainerRef } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PopoverGlobalService } from '@shared/common/popover/popover-global.service';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';

const positionTop: ConnectedPosition = {
    originX: 'center',
    originY: 'top',
    overlayX: 'center',
    overlayY: 'bottom',
};

const positionBottom: ConnectedPosition = {
    originX: 'center',
    originY: 'bottom',
    overlayX: 'center',
    overlayY: 'top',
};

@Directive({ selector: '[appPopover]' })
@UntilDestroy()
export class PopoverDirective implements OnInit, AfterViewInit, OnDestroy {
    private isDestroyed = false;

    @Input() appPopover: TemplateRef<any>;
    @Input() appPopoverEnabled = true;
    @Input() appPopoverContext: any;
    @Input() appPopoverCursor = true;
    @Input() appPopoverDelay = 500;
    @Input() appPopoverBackdrop = false;
    @Input() appPopoverShowOnHover = true;
    @Input() appPopoverPosition: 'top' | 'bottom' = 'top';

    mouseInside$ = new Subject<boolean>();

    private overlayRef: OverlayRef | undefined;

    constructor(
        private _ngZone: NgZone,
        private el: ElementRef,
        private renderer: Renderer2,
        private overlay: Overlay,
        private viewContainerRef: ViewContainerRef,
        private globalService: PopoverGlobalService,
    ) {
    }

    ngOnInit(): void {
        this.mouseInside$
            .pipe(
                filter(() => this.appPopoverShowOnHover),
                filter(() => this.appPopoverEnabled),
                untilDestroyed(this),
                distinctUntilChanged(),
                debounceTime(this.appPopoverDelay),
            )
            .subscribe(inside => {
                if (inside) {
                    this.open();
                } else {
                    this.close();
                }
            });

        if (this.appPopoverEnabled && this.appPopoverCursor) {
            this.renderer.setStyle(this.el.nativeElement, 'cursor', 'help');
        }
    }

    ngAfterViewInit(): void {
        this._ngZone.runOutsideAngular(() => {
            this.el.nativeElement.addEventListener('mouseenter', this.onMouseEnter);
            this.el.nativeElement.addEventListener('pointerdown', this.onPointerDown);
            this.el.nativeElement.addEventListener('mouseleave', this.onMouseLeave);
        });
    }

    ngOnDestroy(): void {
        this.isDestroyed = true;

        this.close();

        this.overlayRef?.dispose();
        this.overlayRef = null;

        this._ngZone.runOutsideAngular(() => {
            this.el.nativeElement.removeEventListener('mouseenter', this.onMouseEnter);
            this.el.nativeElement.removeEventListener('pointerdown', this.onPointerDown);
            this.el.nativeElement.removeEventListener('mouseleave', this.onMouseLeave);
        });
    }

    onMouseEnter = () => {
        if (!this.appPopoverEnabled) {
            return;
        }

        this.mouseInside$.next(true);
    };

    onPointerDown = () => {
        this.close();
    };

    onMouseLeave = ($event: MouseEvent) => {
        this.mouseInside$.next(false);

        this.close();
    };

    public close(): void {
        const ref = this.overlayRef;

        if (!ref) {
            return;
        }

        this._ngZone.run(() => {
            ref.detach();
            this.overlayRef = null;
        });
    }

    open() {
        this._ngZone.run(() => {
            if (!this.appPopoverEnabled || this.isDestroyed || this.globalService.isPointerDown) {
                return;
            }

            const overlayRef = this.overlayRef = this.getOverlayRef();

            const portal = new TemplatePortal(
                this.appPopover,
                this.viewContainerRef,
                {
                    $implicit: this.appPopoverContext,
                    updatePosition: () => requestAnimationFrame(() => overlayRef?.updatePosition())
                }
            );

            this.overlayRef.attach(portal);
        });
    }

    private getOverlayRef() {
        const position: ConnectedPosition[] = this.appPopoverPosition === 'top' ? [positionTop, positionBottom] : [positionBottom, positionTop];

        return this.overlayRef ?? this.overlay.create({
            disposeOnNavigation: true,
            panelClass: 'app-popover-panel',
            backdropClass: 'app-popover-backdrop',
            hasBackdrop: this.appPopoverBackdrop,
            scrollStrategy: this.overlay.scrollStrategies.reposition({ scrollThrottle: 20, autoClose: true }),
            positionStrategy: this.overlay.position()
                .flexibleConnectedTo(this.el)
                .withDefaultOffsetY(-8)
                .withViewportMargin(16)
                .withGrowAfterOpen(true)
                .withLockedPosition(false)
                .withPositions(position),
        });
    }
}
